import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGlobalSettings } from '../hooks/useGlobalSettings';
import { applyBrandName, getBrandName } from '../utils/branding';

const loginSchema = z.object({
  email: z.string().email('E-mail inválido'),
  senha: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const features = [
  'Certidões automáticas com IA',
  'Banco de certidões com IA',
  'Envio de mensagens em massa',
  'Reenvio de mensagens estratégico',
  'Banco de mensagens salvas na plataforma',
  'Gestão por categorias de destinatários',
];

const metrics = [
  { value: '+5k', label: 'mandados processados' },
  { value: '98%', label: 'precisão da IA' },
  { value: '24/7', label: 'disponível' },
];

function SparkIcon() {
  return (
    <svg className="h-5 w-5 text-amber-300" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3 13.8 8.2 19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" fill="currentColor" />
    </svg>
  );
}

function JusticeScaleIcon() {
  return (
    <svg className="h-7 w-7 text-white" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3v18" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      <path d="M7 6h10" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      <path d="M8 6 5 12h6L8 6Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M16 6 13 12h6l-3-6Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M6 21h12" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-300" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="m5 12 4 4 10-10" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function EyeIcon({ open }: { open: boolean }) {
  if (open) {
    return (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 15.5A3.5 3.5 0 1 0 12 8.5a3.5 3.5 0 0 0 0 7Z" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    );
  }

  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="m3 3 18 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M6.2 6.7C3.9 8.4 2.5 12 2.5 12s3.5 7 9.5 7c1.7 0 3.1-.4 4.4-1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.5 4.8C10.3 4.6 11.1 4.5 12 4.5c6 0 9.5 7.5 9.5 7.5a18.3 18.3 0 0 1-2.7 3.9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg className="h-4 w-4 text-slate-500" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3 4.5 6v5.4c0 4.6 3.2 8.9 7.5 9.9 4.3-1 7.5-5.3 7.5-9.9V6L12 3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M9.6 12.1 11.3 14l3.2-3.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function LoginPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const { login } = useAuth();
  const { settings } = useGlobalSettings();
  const brandName = getBrandName();
  const pageTitle = applyBrandName(settings?.pageTitle) || brandName;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true);
    try {
      await login(data.email, data.senha);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-7xl items-center justify-center">
        <div className="w-full overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-[0_24px_70px_rgba(2,6,23,0.5)] backdrop-blur-xl">
          <div className="grid lg:grid-cols-[1.08fr_0.92fr]">
            <section
              className="relative overflow-hidden p-6 text-white sm:p-9 lg:p-10"
              style={{ background: 'linear-gradient(145deg, #071326 0%, #0C2747 48%, #0D1B2F 100%)' }}
            >
              <div className="absolute -left-16 -top-16 h-52 w-52 rounded-full bg-sky-300/10 blur-3xl" />
              <div className="absolute -bottom-20 right-6 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />

              <div className="relative space-y-8">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/15 bg-white/10">
                    {settings?.iconUrl ? (
                      <img
                        src={settings.iconUrl}
                        alt={brandName}
                        className="h-7 w-7 object-contain"
                        style={{ filter: 'brightness(0) invert(1)' }}
                      />
                    ) : (
                      <JusticeScaleIcon />
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-100/85">{pageTitle}</p>
                    <p className="text-sm text-sky-100/75">Acelerador de Cumprimento de Mandados</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h1 className="text-3xl font-semibold leading-tight text-white sm:text-4xl lg:text-5xl">
                    Automatize o Cumprimento de Mandados com IA
                  </h1>
                  <p className="max-w-2xl text-sm leading-7 text-slate-100/90 sm:text-base">
                    Emissão de certidões, envio de mensagens e gestão inteligente em um só lugar.
                  </p>
                </div>

                <div className="relative overflow-hidden rounded-2xl border border-amber-300/30 bg-gradient-to-r from-amber-400/18 via-orange-300/12 to-white/8 p-4 shadow-[0_18px_50px_rgba(251,191,36,0.14)]">
                  <div className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-amber-200/20 blur-2xl" />
                  <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="space-y-2">
                      <div className="inline-flex items-center gap-2 rounded-full border border-amber-200/25 bg-slate-950/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-100">
                        <SparkIcon />
                        Novo destaque
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-white sm:text-xl">
                          Leitor de Mandado em PDF por IA
                        </p>
                        <p className="mt-1 max-w-xl text-sm leading-6 text-amber-50/90">
                          Envie o PDF do mandado e deixe a IA identificar, organizar e acelerar a leitura das informações essenciais.
                        </p>
                      </div>
                    </div>
                    <div className="inline-flex w-fit rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium text-white/90">
                      Recurso em destaque
                    </div>
                  </div>
                </div>

                <ul className="grid gap-3 sm:grid-cols-2">
                  {features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5 rounded-xl border border-white/10 bg-white/5 px-3.5 py-3 text-sm text-slate-100">
                      <CheckIcon />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="grid gap-3 sm:grid-cols-3">
                  {metrics.map((metric) => (
                    <div key={metric.label} className="rounded-xl border border-white/10 bg-slate-950/25 px-4 py-3.5">
                      <p className="text-2xl font-semibold text-white">{metric.value}</p>
                      <p className="mt-1 text-xs text-slate-300">{metric.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="bg-white p-6 sm:p-9 lg:p-10">
              <div className="mx-auto max-w-md">
                <div className="space-y-2">
                  <h2 className="text-3xl font-semibold tracking-tight text-slate-900">Bem-vindo(a)!</h2>
                  <p className="text-sm text-slate-500 sm:text-base">Acesse sua conta para continuar</p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5">
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-slate-700">
                      E-mail
                    </label>
                    <input
                      {...register('email')}
                      type="email"
                      id="email"
                      autoComplete="email"
                      placeholder="seuemail@tribunal.gov.br"
                      disabled={isSubmitting}
                      className={`w-full rounded-xl border bg-slate-50 px-4 py-3 text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:bg-white focus:ring-4 ${
                        errors.email
                          ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-100'
                          : 'border-slate-200 focus:border-slate-400 focus:ring-slate-100'
                      }`}
                    />
                    {errors.email && <p className="text-sm text-rose-600">{errors.email.message}</p>}
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="senha" className="text-sm font-medium text-slate-700">
                      Senha
                    </label>
                    <div className="relative">
                      <input
                        {...register('senha')}
                        type={showPassword ? 'text' : 'password'}
                        id="senha"
                        autoComplete="current-password"
                        placeholder="••••••••"
                        disabled={isSubmitting}
                        className={`w-full rounded-xl border bg-slate-50 px-4 py-3 pr-12 text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:bg-white focus:ring-4 ${
                          errors.senha
                            ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-100'
                            : 'border-slate-200 focus:border-slate-400 focus:ring-slate-100'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((value) => !value)}
                        className="absolute inset-y-0 right-0 flex items-center px-4 text-slate-400 transition hover:text-slate-700"
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      >
                        <EyeIcon open={showPassword} />
                      </button>
                    </div>
                    {errors.senha && <p className="text-sm text-rose-600">{errors.senha.message}</p>}
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <label className="inline-flex items-center gap-3 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(event) => setRememberMe(event.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-600"
                      />
                      Lembrar de mim
                    </label>

                    <button type="button" className="text-sm font-medium text-slate-600 transition hover:text-slate-900">
                      Esqueci minha senha
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-sky-700 via-blue-700 to-indigo-700 px-5 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-900/20 transition-all duration-200 hover:-translate-y-0.5 hover:from-sky-800 hover:via-blue-800 hover:to-indigo-800 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-3">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                        Entrando...
                      </span>
                    ) : (
                      'Acessar plataforma'
                    )}
                  </button>
                </form>

                <div className="my-6 flex items-center gap-4">
                  <div className="h-px flex-1 bg-slate-200" />
                  <span className="text-xs uppercase tracking-[0.14em] text-slate-400">ou</span>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>

                <p className="text-center text-sm text-slate-600">
                  Não tem acesso?{' '}
                  <Link to="/cadastro" className="font-semibold text-blue-700 transition hover:text-blue-800">
                    Solicitar acesso
                  </Link>
                </p>

                <div className="mt-8 flex items-center justify-center gap-2 text-center text-xs text-slate-500">
                  <ShieldIcon />
                  <p>Ambiente seguro • Dados protegidos • LGPD</p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
